import { Link } from "react-router-dom";
import { Instagram, Phone, MapPin } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";
import { useMall } from "@/context/MallContext";
import { SHOP_NAV } from "@/lib/nav";

/** The site index, three drawers: what you can buy, how to rent this site, and our custom web work. */
const GROUPS = (phoneRaw, shop) => [
    {
        key: "shop", label: "Shop", note: "Everything a customer can order",
        links: [
            ...SHOP_NAV.map((n) => ({ key: n.key, to: n.to, label: n.label })),
            { key: "dtf", to: "/shirts/dtf/gang-sheet", label: "DTF Gang Sheet Builder" },
            { key: "artwork", to: "/artwork-request", label: "Artwork Request" },
            { key: "track", to: "/track", label: "Track an Order" },
            ...(shop ? [{ key: "policies", to: `/stores/${shop.slug}/policies`, label: `${shop.name} shop policies` }] : []),
            { key: "directory", to: "/directory", label: "Shop directory" },
        ],
    },
    {
        key: "own", label: "Own a print shop?", note: "Rent this website with your name on it",
        blurb: "13thav rents out white-label print shop websites. You get your own website, just like the one you are on right now, with the same live designers, and it carries your name, your logo, your colors and your web address on every page. Your prices go into your own Stripe account. You print what you make yourself, and 13thav prints the rest and pays you 20% of each sale. A shop page in the directory starts at $9 a month. The whole website as your own private site is $199 a month.",
        links: [
            { key: "whitelabel", to: "/whitelabel", label: "Get this website for your shop →", strong: true },
            { key: "what", to: "/whitelabel#what-you-get", label: "What you get" },
            { key: "modes", to: "/whitelabel#modes", label: "Wizard Mode & Consignment Mode" },
            { key: "pricing", to: "/whitelabel#pricing", label: "Plans & pricing" },
            { key: "call", to: "/whitelabel#book-a-call", label: "Book a 10-minute setup call" },
            { key: "sell", to: "/supplies/sell", label: "Post a classified ad" },
            { key: "services", to: "/services", label: "Machine service" },
        ],
    },
    {
        key: "web", label: "Website Design", note: "Custom sites for any business, live in 7 days",
        blurb: "We also build fully custom websites for any business, not just print shops. We build them with AI and they go live in 7 days. Scroll the demo playground and try every feature before you buy.",
        links: [
            { key: "web", to: "/website-design", label: "See the demo playground →", strong: true },
            { key: "process", to: "/website-design#process", label: "How the 7 days work" },
            { key: "quote", to: "/website-design#quote", label: "Get a quote" },
            { key: "text", href: `sms:${phoneRaw}?body=I%20want%20a%20new%20website.`, label: "Text us about a site" },
        ],
    },
];

/** One section of the site index — always open, so every link is one click away. */
function Drawer({ group }) {
    return (
        <div className="border-t border-white/10 md:border-t-0" data-testid={`footer-group-${group.key}`}>
            <div className="py-3 md:py-0" data-testid={`footer-group-toggle-${group.key}`}>
                <span className="block font-mono text-xs uppercase tracking-widest text-zinc-400">{group.label}</span>
                <span className="mt-0.5 block text-xs text-zinc-500">{group.note}</span>
            </div>
            <div className="pb-4 md:pt-3" data-testid={`footer-group-links-${group.key}`}>
                {group.blurb && <p className="mb-3 text-xs leading-relaxed text-zinc-500" data-testid={`footer-blurb-${group.key}`}>{group.blurb}</p>}
                <ul className="space-y-2 text-sm text-zinc-300">
                    {group.links.map((l) => (
                        <li key={l.key}>
                            {l.href
                                ? <a href={l.href} className="hover:text-white">{l.label}</a>
                                : <Link to={l.to} data-testid={`footer-nav-${l.key}`}
                                    className={l.strong ? "font-semibold text-white hover:underline" : "hover:text-white"}>{l.label}</Link>}
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
}

export default function Footer() {
    const settings = useSettings();
    const { solo, site, store } = useMall();
    const b = settings?.business;
    const shop = store || site?.store;
    const groups = GROUPS(b?.phone_raw || "+13475733875", shop);

    return (
        <footer className="dark-block relative border-t border-white/10 bg-brand-ink pt-16" data-testid="site-footer">
            <div className="mx-auto max-w-7xl px-4 sm:px-6">
                <div className="grid gap-10 md:grid-cols-[1.2fr_1fr_1fr_1fr]">
                    <div data-testid="footer-house-block">
                        <Link to="/whitelabel" className="inline-flex items-center gap-2" data-testid="footer-logo">
                            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-white font-display text-base font-black text-black">13</span>
                            <span className="font-display text-2xl font-black">13thav<span className="text-[#60A5FA]">.</span></span>
                        </Link>
                        <p className="mt-3 max-w-xs text-sm text-zinc-400">13thav rents white-label websites to print shops. This directory lists the shops that run on them.</p>
                        <p className="mt-2 font-mono text-xs uppercase tracking-widest text-zinc-500">Brooklyn · {b?.established}</p>
                        <ul className="mt-5 space-y-3 text-sm text-zinc-300">
                            <li className="flex items-start gap-2"><MapPin size={16} className="mt-0.5 shrink-0 text-white" /> {b?.address}</li>
                            <li><a href={`tel:${b?.phone_raw}`} className="flex items-center gap-2 hover:text-white"><Phone size={16} className="text-white" /> {b?.phone}</a></li>
                            <li><a href={b?.socials?.instagram} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-white"><Instagram size={16} className="text-white" /> @13thav</a></li>
                        </ul>
                    </div>
                    <div className="md:col-span-3">
                        <p className="mb-2 font-mono text-[11px] uppercase tracking-[0.28em] text-zinc-500" data-testid="footer-index-title">Site index</p>
                        <div className="grid gap-x-8 md:grid-cols-3" data-testid="footer-site-index">
                            {groups.map((g) => <Drawer key={g.key} group={g} />)}
                        </div>
                    </div>
                </div>
            </div>
            <div className="mt-14 overflow-hidden">
                <div className="select-none text-center font-display text-[19vw] font-black leading-[0.8] tracking-tighter text-white/[0.04]">13THAV</div>
            </div>
            <div className="border-t border-white/10 py-6">
                {solo ? (
                    <p className="text-center text-xs text-zinc-500" data-testid="footer-solo-line">
                        © {new Date().getFullYear()} {site?.store?.name || "This shop"}
                        {site?.store?.city ? ` · ${site.store.city}` : ""} ·{" "}
                        <Link to="/whitelabel" className="font-semibold text-zinc-300 underline hover:text-white" data-testid="footer-powered-by">
                            Powered by 13thav — get a site like this
                        </Link>
                        {" "}·{" "}
                        <a href="/site/exit" className="underline hover:text-zinc-300" data-testid="footer-solo-exit">
                            shop directory
                        </a>
                    </p>
                ) : (
                    <p className="text-center text-xs text-zinc-500">© {new Date().getFullYear()} 13thav.com · Brooklyn, NY · White-label websites for print shops</p>
                )}
            </div>
        </footer>
    );
}
